import React, { useCallback } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';

import { CreateRateGroupRuleBody } from '~/services/cruises/RateGroupRuleService';

interface Props {
  rateGroupRuleForm: CreateRateGroupRuleBody;
  setRateGroupRuleForm: (rateGroupRuleForm: CreateRateGroupRuleBody) => void;
}

const LIMIT_FILTERS = 4;

function RateGroupRulesFilters({ rateGroupRuleForm, setRateGroupRuleForm }: Props) {
  const setFilter = useCallback(
    (index: number, filter: CreateRateGroupRuleBody['filters'][number]) => {
      const newFilters = [...rateGroupRuleForm.filters];
      newFilters[index] = filter;
      setRateGroupRuleForm({ ...rateGroupRuleForm, filters: newFilters });
    },
    [rateGroupRuleForm, setRateGroupRuleForm],
  );

  const handleAddFilter = useCallback(() => {
    setRateGroupRuleForm({
      ...rateGroupRuleForm,
      filters: [...rateGroupRuleForm.filters, { filterType: 'RATE_CODE', rateCodePattern: '', matchType: 'PREFIX' }],
    });
  }, [rateGroupRuleForm, setRateGroupRuleForm]);

  const handleDeleteFilter = useCallback(
    (index: number) => {
      const newFilters = [...rateGroupRuleForm.filters];
      newFilters.splice(index, 1);
      setRateGroupRuleForm({ ...rateGroupRuleForm, filters: newFilters });
    },
    [rateGroupRuleForm, setRateGroupRuleForm],
  );

  return (
    <Stack direction="column" spacing={1}>
      {rateGroupRuleForm.filters.map((filter, index) => (
        <>
          <Stack direction="row" spacing={2} key={index} alignItems="center">
            <FormControl fullWidth>
              <InputLabel id="filter-type-label">Filter Type</InputLabel>
              <Select<CreateRateGroupRuleBody['filters'][number]['filterType']>
                labelId="filter-type-label"
                value={filter.filterType}
                label="Filter Type"
                onChange={(e) =>
                  setFilter(index, {
                    ...filter,
                    filterType: e.target.value as CreateRateGroupRuleBody['filters'][number]['filterType'],
                  })
                }
              >
                <MenuItem value="RATE_CODE">Rate Code</MenuItem>
                <MenuItem value="RATE_CODE_DESCRIPTION">Rate Code Description</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Rate Pattern"
              value={filter.rateCodePattern}
              onChange={(e) => setFilter(index, { ...filter, rateCodePattern: e.target.value })}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel id="match-type-label">Match Type</InputLabel>
              <Select
                labelId="match-type-label"
                value={filter.matchType}
                label="Match Type"
                onChange={(e) =>
                  setFilter(index, {
                    ...filter,
                    matchType: e.target.value as CreateRateGroupRuleBody['filters'][number]['matchType'],
                  })
                }
              >
                <MenuItem value="PREFIX">Prefix</MenuItem>
                <MenuItem value="SUFFIX">Suffix</MenuItem>
                <MenuItem value="EXACT">Exact</MenuItem>
                <MenuItem value="CONTAINS">Contains</MenuItem>
              </Select>
            </FormControl>

            {rateGroupRuleForm.filters.length > 1 && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => handleDeleteFilter(index)}
                disabled={index === 0}
              >
                <DeleteIcon />
              </Button>
            )}
          </Stack>

          {rateGroupRuleForm.filters.length > 1 && index < rateGroupRuleForm.filters.length - 1 && (
            <Box>
              <Typography variant="body2" fontWeight="bold" textAlign="center">
                AND
              </Typography>
            </Box>
          )}
        </>
      ))}

      <Button
        size="small"
        variant="outlined"
        onClick={handleAddFilter}
        disabled={rateGroupRuleForm.filters.length >= LIMIT_FILTERS}
      >
        Add Rule Filter {rateGroupRuleForm.filters.length} / {LIMIT_FILTERS}
      </Button>
    </Stack>
  );
}

export default RateGroupRulesFilters;
